import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { cs } from '@shared/utils/utils-classname';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { Button } from '@shared/ui-react/Button';

export const DialogUtils = {
    renderComponentBasic: (title: string, body: any) => {
        DialogUtils.renderComponent({ title, body });
    },

    renderHeaderless: (body: any) => {
        DialogUtils.renderComponent({ title: '', body, showHeader: false });
    },

    renderComponent: (opts: { title: string; body: any; onConfirm?: Function; confirmBtnText?: string; el?: HTMLElement; showHeader?: boolean }) => {
        const onConfirm = () => {
            opts.onConfirm();
            DialogUtils.close();
        };

        const close = () => {
            ReactUtils.safeUnmountComponentAtNode(opts.el || document.getElementById('react-portal-root'));
        };

        ReactUtils.safeRender(
            <Dialog onClose={close} title={opts.title} closeOnEsc={true} showHeader={opts.showHeader !== false}>
                {opts.body}
                {typeof opts.onConfirm === 'function' && (
                    <footer style={{ padding: '15px 25px 15px', background: 'white', border: '1px solid #EFEFEF' }}>
                        <Button className='ik-button-secondary--subtle' onClick={DialogUtils.close}>
                            {__('general.close')}
                        </Button>
                        <Button className='ik-button-primary right' onClick={onConfirm}>
                            {opts.confirmBtnText}
                        </Button>
                    </footer>
                )}
            </Dialog>,
            opts.el || document.getElementById('react-portal-root')
        );
    },

    close: (elId = 'react-portal-root') => {
        if (typeof elId !== 'string') {
            // could be some random event when function is passed as reference
            elId = 'react-portal-root';
        }
        ReactUtils.safeUnmountComponentAtNode(document.getElementById(elId));
    },
};

let OPENED_DIALOGS_COUNT = 0;

export class Dialog extends React.PureComponent<{
    title?: string;
    onClose: () => void;
    immediateClose: boolean;
    showMaximizeButton?: boolean;
    animated?: boolean;
    showHeader?: boolean;
    closeOnEsc?: boolean;
    children?: React.ReactNode;
}> {
    state = {
        isOpen: false,
        isMaximized: false,
    };

    static defaultProps = {
        animated: true,
        showMaximizeButton: false,
        showHeader: true,
        immediateClose: true,
        closeOnEsc: false,
    };

    zIndex = 9400;

    componentDidMount(): void {
        console.log('OPEN');
        OPENED_DIALOGS_COUNT += 1;
        this.zIndex = 9400 + OPENED_DIALOGS_COUNT;

        setTimeout(() => {
            // needed to trigger open animation
            this.setState({ isOpen: true });
        }, 1);

        if (this.props.closeOnEsc) {
            window.addEventListener('keydown', this.keyboardHandler);
        }
    }

    componentWillUnmount(): void {
        window.removeEventListener('keydown', this.keyboardHandler);
        OPENED_DIALOGS_COUNT -= 1;
    }

    render() {
        const { children, title, animated, showMaximizeButton } = this.props;

        const bodyClass = cs('infrakit-dialog__bg', {
            animated,
            open: this.state.isOpen,
        });

        const dialogClass = cs('infrakit-dialog', {
            maximized: this.state.isMaximized,
        });

        return ReactDOM.createPortal(
            <div className={bodyClass} style={{ zIndex: this.zIndex }} data-dialog-id={this.zIndex}>
                <section className={dialogClass} style={{ width: 'auto', maxWidth: 'none' }} onTransitionEnd={this.handleCloseCallback}>
                    {this.props.showHeader && (
                        <header className='infrakit-dialog__header'>
                            <span>{title}</span>
                            {showMaximizeButton && (
                                <div className='infrakit-dialog__btn infrakit-dialog__expand' onClick={this.toggleMaximize}>
                                    <i className='iki iki-expand' />
                                </div>
                            )}
                            <div className='infrakit-dialog__btn infrakit-dialog__close' onClick={this.handleCloseRequest}>
                                <i className='iki iki-cross' />
                            </div>
                        </header>
                    )}

                    <main className='infrakit-dialog__body'>{children}</main>
                </section>
            </div>,
            document.body
        );
    }

    handleCloseRequest = () => {
        if (this.props.immediateClose) {
            if (!this.state.isOpen) return;
            this.setState({ isOpen: false });

            // when animated, animationEnd even will close the dialog, otherwise we do it manually
            if (!this.props.animated) {
                this.handleCloseCallback();
            }
        } else {
            this.props.onClose();
        }
    };

    handleCloseCallback = () => {
        if (this.state.isOpen) return;
        this.props.onClose();
    };

    toggleMaximize = () => {
        this.setState({ isMaximized: !this.state.isMaximized });
    };

    keyboardHandler = e => {
        if (e.key === 'Escape') {
            const allDialogIds = Array.from(document.querySelectorAll('[data-dialog-id]')).map(e => parseInt((e as any).dataset.dialogId));
            if (allDialogIds.length === 1 || allDialogIds.filter(id => id !== this.zIndex).every(id => id < this.zIndex)) {
                this.props.onClose();
            }
        }
    };
}
